import { useMemo, useCallback } from "react";
import { useUserData } from "../../../hooks/useUserData";
import {
  parsePermissions,
  hasPermission,
  getAllPermissionKeys,
} from "../../../utils/permissionUtils";
import {
  QMS_ACTION_LEGACY_ALIASES,
  QMS_ACTION_PERMISSIONS,
  QMS_ALL_PERMISSIONS,
  QMS_CONSOLIDATED_PARENT_GRANTS,
} from "../../../constants/qmsPermissions";
import { LEGACY_TARGET_VIEW_TO_PERMISSION } from "./qmsScreenConfig";
import { organizationHasQmsModule } from "../../../utils/orgModuleAccess";

export { organizationHasQmsModule };

/** Admin + legacy MVC TargetView ids that grant the same screen as QMS_* keys. */
const QMS_PERMISSION_ALIASES = {
  // Doc type/category live under QMS Config — QMS_CONFIGURE is enough.
  QMS_DOCUMENT_TYPE: ["QMS_CONFIGURE"],
  QMS_DOCUMENT_CATEGORY: ["QMS_CONFIGURE"],
  // Legacy only: quiz lived under My Vault / Publish; granular quiz keys also open Quiz Manager.
  QMS_QUESTION_BANK: [
    "QMS_VAULT",
    "QMS_PUBLISH_DOCUMENTS",
    "QMS_QUIZ_MANAGER",
    "QMS_QUIZ_ADD_EDIT",
    "QMS_QUIZ_DELETE",
    "QMS_QUESTION_ADD_EDIT",
    "QMS_QUESTION_DELETE",
    "QMS_QUESTION_IMPORT_BULK",
  ],
  QMS_QUIZ_MANAGER: [
    "QMS_VAULT",
    "QMS_PUBLISH_DOCUMENTS",
    "QMS_QUIZ_ADD_EDIT",
    "QMS_QUIZ_DELETE",
    "QMS_QUESTION_ADD_EDIT",
    "QMS_QUESTION_DELETE",
    "QMS_QUESTION_IMPORT_BULK",
  ],
  // Legacy Tracker / Transfer / Archive pages (and Document Control) open the same screen.
  QMS_DOCUMENT_CONTROL: [
    "QMS_TRACKER",
    "DMS_TRACKER",
    "QMS_TRANSFER_OWNERSHIP",
    "DMS_TRANSFEROWNERSHIP",
    "QMS_ARCHIVE_DOCUMENTS",
    "DMS_ARCHIVEDOCUMENTS",
    "QMS_UNARCHIVE_DOCUMENTS",
  ],
};

function parentGrantsAction(userPermissions, actionKey) {
  return Object.entries(QMS_CONSOLIDATED_PARENT_GRANTS).some(
    ([parentKey, childKeys]) =>
      (childKeys || []).includes(actionKey) &&
      hasPermission(userPermissions, parentKey),
  );
}

export function getQmsPermissionKeys(permissionKey) {
  if (!permissionKey) return [];
  const legacyTargetViews = Object.entries(LEGACY_TARGET_VIEW_TO_PERMISSION)
    .filter(([, mapped]) => mapped === permissionKey)
    .map(([targetView]) => targetView);
  const aliases = QMS_PERMISSION_ALIASES[permissionKey] || [];
  return [permissionKey, ...legacyTargetViews, ...aliases];
}

export function hasQmsScreenPermission(userPermissions, permissionKey) {
  if (!permissionKey) return true;
  return getQmsPermissionKeys(permissionKey).some((key) =>
    hasPermission(userPermissions, key),
  );
}

export function getQmsActionPermissionKeys(actionKey) {
  if (!actionKey) return [];
  const legacyAliases = QMS_ACTION_LEGACY_ALIASES[actionKey] || [];
  return [actionKey, ...legacyAliases];
}

export function hasQmsActionPermission(userPermissions, actionKey) {
  if (!actionKey) return true;
  if (
    getQmsActionPermissionKeys(actionKey).some((key) =>
      hasPermission(userPermissions, key),
    )
  ) {
    return true;
  }
  return parentGrantsAction(userPermissions, actionKey);
}

/** Shared With Me is for reviewers and approvers (legacy page key is not required). */
export function canAccessQmsSharedWithMe(userPermissions, isSystemAdmin = false) {
  if (isSystemAdmin) return true;
  return (
    hasQmsActionPermission(userPermissions, "QMS_CAN_REVIEW_DOCUMENTS") ||
    hasQmsActionPermission(userPermissions, "QMS_CAN_APPROVE_DOCUMENTS")
  );
}

export const QMS_ACTION_PERMISSION_KEYS = QMS_ACTION_PERMISSIONS.map((p) => p.key);

/**
 * Map legacy PageAction labels / DMS_* keys to canonical QMS_* keys when loading
 * role/user permissions for the permission card.
 *
 * Also consolidates Quiz Manager / Document Control children into the parent key.
 */
export function expandStoredQmsPermissionKeys(permissionKeys = []) {
  const normalized = (permissionKeys || [])
    .map((key) => String(key || "").trim())
    .filter(Boolean);
  const result = new Set(normalized);

  for (const [canonicalKey, legacyAliases] of Object.entries(
    QMS_ACTION_LEGACY_ALIASES,
  )) {
    const hasTrueLegacy = (legacyAliases || []).some((alias) => {
      const a = String(alias || "").trim();
      if (!a || !result.has(a)) return false;
      // Only promote old labels / DMS_* — never treat another QMS_* as a parent grant.
      return !a.startsWith("QMS_");
    });
    if (hasTrueLegacy) {
      result.add(canonicalKey);
    }
  }

  for (const [parentKey, childKeys] of Object.entries(
    QMS_CONSOLIDATED_PARENT_GRANTS,
  )) {
    const hasParent = result.has(parentKey);
    const hasChild = (childKeys || []).some((key) => result.has(key));
    if (hasParent || hasChild) {
      result.add(parentKey);
      (childKeys || []).forEach((key) => result.delete(key));
    }
  }

  return Array.from(result);
}

/** Screens every user can open when the org has QMS — no explicit role permission required. */
export const QMS_ORG_DEFAULT_SCREEN_PERMISSIONS = [
  "QMS_MY_LEARNING",
  "QMS_DASHBOARD",
];

export function isQmsOrgDefaultScreen(permissionKey) {
  return QMS_ORG_DEFAULT_SCREEN_PERMISSIONS.includes(permissionKey);
}

const QMS_PERMISSION_KEYS = QMS_ALL_PERMISSIONS.map((item) => item.key);

export function canAccessQmsRoute({ userData, isSystemAdmin, permissionKey }) {
  const access = getQmsAccessState({
    userData,
    isSystemAdmin,
    permissionKey,
    loading: false,
  });
  return access.status === "allowed";
}

/**
 * Avoid redirecting before session loads; distinguish module vs permission denial.
 */
export function getQmsAccessState({
  userData,
  isSystemAdmin,
  permissionKey,
  loading = false,
}) {
  if (loading || !userData) {
    return { status: "loading" };
  }

  if (!organizationHasQmsModule(userData) && !isSystemAdmin) {
    return {
      status: "denied",
      reason: "module",
      message:
        "QMS is not enabled for this organisation. Contact your administrator.",
    };
  }

  if (isSystemAdmin) {
    return { status: "allowed" };
  }

  if (permissionKey) {
    const perms = parsePermissions(userData?.Permission || "");
    if (permissionKey === "QMS_SHARED_WITH_ME") {
      if (!canAccessQmsSharedWithMe(perms, false)) {
        return {
          status: "denied",
          reason: "permission",
          message:
            "Shared With Me is available only to users who can review or approve documents. Ask your administrator to assign the correct QMS workflow permissions.",
        };
      }
    } else if (
      isQmsOrgDefaultScreen(permissionKey) &&
      organizationHasQmsModule(userData)
    ) {
      // My Learning is available to every user when the organisation has QMS.
    } else if (!hasQmsScreenPermission(perms, permissionKey)) {
      return {
        status: "denied",
        reason: "permission",
        message:
          "You do not have permission to view this screen. Ask your administrator to assign the correct QMS role permissions.",
      };
    }
  }

  return { status: "allowed" };
}

export function useQmsAccess() {
  const { userData } = useUserData();
  const isSystemAdmin = userData?.Role === "System Administrator";
  const orgHasQms = organizationHasQmsModule(userData);

  const perms = useMemo(
    () =>
      isSystemAdmin
        ? getAllPermissionKeys()
        : parsePermissions(userData?.Permission || ""),
    [isSystemAdmin, userData?.Permission],
  );

  const can = useCallback(
    (key) => {
      if (isSystemAdmin) return true;
      if (orgHasQms && isQmsOrgDefaultScreen(key)) return true;
      if (hasQmsScreenPermission(perms, key)) return true;
      // Consolidated parents (Quiz Manager / Document Control) grant child screens.
      return parentGrantsAction(perms, key);
    },
    [isSystemAdmin, orgHasQms, perms],
  );

  const screenAccess = useMemo(() => {
    const out = {};
    for (const key of QMS_PERMISSION_KEYS) {
      if (key === "QMS_SHARED_WITH_ME") {
        out[key] = canAccessQmsSharedWithMe(perms, isSystemAdmin);
      } else if (isQmsOrgDefaultScreen(key)) {
        out[key] = isSystemAdmin || orgHasQms || hasQmsScreenPermission(perms, key);
      } else {
        out[key] = can(key);
      }
    }
    return out;
  }, [can, isSystemAdmin, orgHasQms, perms]);

  return {
    userData,
    isSystemAdmin,
    orgHasQms,
    canAccessQmsModule: isSystemAdmin || orgHasQms,
    screenAccess,
    canQmsDashboard: can("QMS_DASHBOARD"),
    canQmsDashboardViewAll: can("QMS_DASHBOARD_VIEW_ALL"),
    canQmsComplianceReport: can("QMS_COMPLIANCE_REPORT"),
    canQmsDocumentType: can("QMS_DOCUMENT_TYPE"),
    canQmsDocumentCategory: can("QMS_DOCUMENT_CATEGORY"),
    canQmsVault: can("QMS_VAULT"),
    canQmsPublish: can("QMS_PUBLISH_DOCUMENTS"),
    canQmsLibrary: can("QMS_DOCUMENT_LIBRARY"),
    canQmsCreateChangeRequest: can("QMS_CREATE_CHANGE_REQUEST"),
    canQmsChangeRequests: can("QMS_CHANGE_REQUESTS"),
    canQmsTracker: can("QMS_TRACKER"),
    canQmsTransferOwnership: can("QMS_TRANSFER_OWNERSHIP"),
    canQmsReadingList: can("QMS_READING_LIST"),
    canQmsArchive: can("QMS_ARCHIVE_DOCUMENTS"),
    canQmsCertificateTemplate: can("QMS_CERTIFICATE_TEMPLATE"),
    canQmsQuestionBank: can("QMS_QUESTION_BANK"),
    canQmsQuizManager: can("QMS_QUIZ_MANAGER"),
    canQmsMyLearning: can("QMS_MY_LEARNING"),
    canQmsMyRecords: can("QMS_MY_RECORDS"),
    canQmsSharedWithMe: canAccessQmsSharedWithMe(perms, isSystemAdmin),
    canQmsViewChangeRequest: can("QMS_VIEW_CHANGE_REQUEST"),
    canQmsChangeStatus: can("QMS_CHANGE_STATUS"),
    canQmsReviewDocuments: can("QMS_CAN_REVIEW_DOCUMENTS"),
    canQmsApproveDocuments: can("QMS_CAN_APPROVE_DOCUMENTS"),
    canQmsLibraryViewAssignedStaff: can("QMS_LIBRARY_VIEW_ASSIGNED_STAFF"),
    canQmsLibraryAuditSummary: can("QMS_LIBRARY_AUDIT_SUMMARY"),
    canQmsLibraryAssignStaff: can("QMS_LIBRARY_SEE_STAFF"),
    canQmsLibrarySeeStaff: can("QMS_LIBRARY_SEE_STAFF"),
    canQmsMyLearningAssignStaff: can("QMS_LIBRARY_ADD_STAFF"),
    canQmsLibraryAddStaff: can("QMS_LIBRARY_ADD_STAFF"),
    canQmsDocumentControl: can("QMS_DOCUMENT_CONTROL"),
    // Quiz Manager / Document Control each grant their former granular actions.
    canQmsQuizAddEdit: hasQmsActionPermission(perms, "QMS_QUIZ_ADD_EDIT"),
    canQmsQuizDelete: hasQmsActionPermission(perms, "QMS_QUIZ_DELETE"),
    canQmsQuestionAddEdit: hasQmsActionPermission(perms, "QMS_QUESTION_ADD_EDIT"),
    canQmsQuestionDelete: hasQmsActionPermission(perms, "QMS_QUESTION_DELETE"),
    canQmsQuestionImportBulk: hasQmsActionPermission(
      perms,
      "QMS_QUESTION_IMPORT_BULK",
    ),
    canQmsUnarchive: hasQmsActionPermission(perms, "QMS_UNARCHIVE_DOCUMENTS"),
    hasQmsAction: (actionKey) =>
      hasQmsActionPermission(perms, actionKey) || can(actionKey),
  };
}
